import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useParams, Link } from "react-router-dom";
import { ChevronLeft, ArrowLeftRight, ChevronUp, ChevronDown, Minus, LayoutDashboard, Trophy, Users } from "lucide-react";
import { api } from "@/lib/api";
import { useMe } from "@/lib/useMe";
import { T } from "@/lib/testIds";
import { cn } from "@/lib/utils";
import LeagueDashboard from "@/pages/LeagueDashboard";
import TeamDetailModal from "@/components/app/TeamDetailModal";

const RankArrow = ({ current, last }) => {
  if (!last || last === 0) return <Minus size={12} className="text-totem-textFaint" />;
  if (current < last) return <ChevronUp size={14} className="text-totem-green" />;
  if (current > last) return <ChevronDown size={14} className="text-totem-red" />;
  return <Minus size={12} className="text-totem-textFaint" />;
};

function StandingsTable({ id }) {
  const { data: meData } = useMe();
  const myManagerId = meData?.manager?.id;
  const [selected, setSelected] = useState(null);
  const { data, isLoading, error } = useQuery({
    queryKey: ["league", id],
    queryFn: async () => (await api.get(`/leagues/${id}/standings`)).data,
    staleTime: 60_000,
  });
  if (isLoading) return <div className="totem-card h-64 animate-pulse" />;
  if (error || !data) return <div className="totem-card p-6 text-totem-red">Could not load standings.</div>;
  const rows = data.standings?.results || [];
  return (
    <section data-testid={T.leagueStandings} className="totem-card p-2 animate-fade-up">
      <table className="w-full text-sm">
        <thead>
          <tr>
            <th className="px-3 py-2 text-left label-caps">Rank</th>
            <th className="px-3 py-2 text-left label-caps">Manager</th>
            <th className="px-2 py-2 text-right label-caps">GW</th>
            <th className="px-3 py-2 text-right label-caps">Total</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const isMe = r.entry === myManagerId;
            return (
              <tr
                key={r.entry}
                data-testid={`standings-row-${r.entry}`}
                onClick={() => setSelected({ entry: r.entry, entry_name: r.entry_name, player_name: r.player_name })}
                className={cn(
                  "border-t border-totem-borderSoft cursor-pointer hover:bg-totem-surfaceHigh transition",
                  isMe && "bg-totem-green/5"
                )}
              >
                <td className="px-3 py-2.5">
                  <div className="flex items-center gap-1">
                    <span className="mono">{r.rank}</span>
                    <RankArrow current={r.rank} last={r.last_rank} />
                  </div>
                </td>
                <td className="px-3 py-2.5 min-w-0">
                  <div className={cn("font-semibold truncate", isMe && "text-totem-green")}>
                    {r.entry_name}
                  </div>
                  <div className="mono text-[10px] uppercase text-totem-textDim truncate">
                    {r.player_name}
                  </div>
                </td>
                <td className="px-2 py-2.5 text-right mono">{r.event_total}</td>
                <td className="px-3 py-2.5 text-right mono font-semibold">{r.total}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {rows.length === 0 && (
        <div className="p-6 text-center text-totem-textDim">
          No standings yet — league likely starts at GW1.
        </div>
      )}
      <TeamDetailModal
        open={!!selected}
        onClose={() => setSelected(null)}
        managerId={selected?.entry}
        entryName={selected?.entry_name}
        playerName={selected?.player_name}
      />
    </section>
  );
}

const TABS = [
  { key: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { key: "standings", label: "Standings", icon: Trophy },
];

export default function LeagueDetail() {
  const { id } = useParams();
  const [tab, setTab] = useState("dashboard");
  const { data: header } = useQuery({
    queryKey: ["league-header", id],
    queryFn: async () => (await api.get(`/leagues/${id}/standings`)).data,
    staleTime: 5 * 60_000,
  });
  const league = header?.league;

  return (
    <div className="space-y-4 pt-1 animate-fade-up">
      <div className="flex items-center justify-between">
        <Link
          to="/leagues"
          className="inline-flex items-center gap-1 text-totem-textDim hover:text-totem-green text-sm"
        >
          <ChevronLeft size={16} /> Leagues
        </Link>
        <Link
          to={`/leagues/${id}/diff`}
          data-testid="league-rival-diff-link"
          className="inline-flex items-center gap-1.5 text-xs mono uppercase tracking-wider text-totem-gold bg-totem-gold/10 border border-totem-gold/40 rounded-full px-3 py-1.5 hover:bg-totem-gold/15"
        >
          <Users size={12} /> Rival diff
        </Link>
      </div>

      <section className="totem-card p-5">
        <div className="label-caps mb-1">League</div>
        <h2 className="display-heading text-2xl truncate">{league?.name || "…"}</h2>
        <div className="mono uppercase text-[10px] text-totem-textDim mt-1">
          {league?.league_type === "s" ? "Public" : "Private"} •{" "}
          {league?.created ? `Created ${new Date(league.created).toLocaleDateString()}` : "—"}
        </div>
      </section>

      <div className="grid grid-cols-2 gap-1 p-0.5 bg-totem-surface border border-totem-border rounded-lg">
        {TABS.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            data-testid={`league-tab-${key}`}
            className={cn(
              "mono text-xs uppercase tracking-wider py-2 rounded-md flex items-center justify-center gap-1.5 transition",
              tab === key
                ? "bg-totem-green text-totem-bg font-semibold"
                : "text-totem-textDim"
            )}
          >
            <Icon size={12} />
            {label}
          </button>
        ))}
      </div>

      {tab === "dashboard" ? <LeagueDashboard leagueId={id} /> : <StandingsTable id={id} />}
    </div>
  );
}
